// utils/leadsTelemetryPro.jsx
// Telemetria silenciosa + diagnóstico para Leads.jsx (CAPTA nível)
// Uso típico:
//   import { initLeadsTelemetry, useRenderCounter, appMarkStart, appMarkEnd, TelemetryHUD } from "../utils/leadsTelemetryPro";
//   useEffect(() => initLeadsTelemetry({ empresaId, userId, apiBase: API_URL, tableRootSelector: "#tabela-leads" }), []);
//   useRenderCounter("Leads.jsx");
//   const m = appMarkStart("preprocess:leads", { n: leads.length }); /* ... */ appMarkEnd(m);
//   // HUD no layout: <TelemetryHUD position="bottom-right" refreshMs={3000} />

import React from "react";
// no topo do componente de Leads
<TelemetryHUD position="bottom-right" refreshMs={3000} />;

/* ==================== CONFIG ==================== */
const FLAG_LOCAL = "CAPTA_TELEMETRY";
const FLAG_DEMO = "CAPTA_TELEMETRY_DEMO";

const defaultCfg = {
  sampleRate: 0.15,
  flushIntervalMs: 15000,
  maxBatch: 30,
  maxPayloadBytes: 450 * 1024,
  tableRootSelector: null,

  apiBase: "", // "https://app.captacrm.com.br/api" OU "https://app.captacrm.com.br"
  endpoint: "/telemetria/leads",

  empresaId: null,
  userId: null,
  page: "leads",
  clientVersion: "leads-telemetry-pro-v2",
  networkEnabled: true,
  fpsEnabled: true,
  longTaskEnabled: true,
  domSampleEnabled: true,
  rendersEnabled: true,
};

let cfg = { ...defaultCfg };
let started = false;
let enabled = false;
let netPatched = false;

/* ==================== STORE ==================== */
const store = {
  hidden: false,
  renders: 0,
  renderMsTotal: 0,
  renderMaxMs: 0,
  apiCalls: [], // {method, path, ms, status, size, ts}
  longTasks: [],
  fps: { current: 0, min: 60, max: 0, sum: 0, n: 0 },
  dom: { rows: 0, cells: 0 },
  batch: [],
  stormWinStart: 0,
  stormCount: 0,
  dev: [],
};

const now = () => performance.now?.() ?? Date.now();
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = () => Math.random();

const normalizePath = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.replace(/\/+$/, "") || "/";
  } catch {
    const p = (url || "").split("?")[0] || "/";
    return p.replace(/\/+$/, "") || "/";
  }
};

const joinUrl = (base, path) => {
  if (!base) return path || "";
  const b = base.endsWith("/") ? base.slice(0, -1) : base;
  const p = path?.startsWith("/") ? path : `/${path || ""}`;
  return `${b}${p}`;
};

const resolveEndpoint = () =>
  /\/api\/?$/.test(cfg.apiBase || "")
    ? "/telemetria/leads"
    : "/api/telemetria/leads";

/* ==================== VISIBILIDADE ==================== */
function installVisibilityHandler() {
  const onVis = () => (store.hidden = document.hidden);
  document.addEventListener("visibilitychange", onVis);
  onVis();
}

/* ==================== ERROS JS ==================== */
function sanitizeText(t, max = 300) {
  try {
    const s = (t ?? "").toString();
    return s.length > max ? s.slice(0, max) : s;
  } catch {
    return "";
  }
}
function installErrorHandlers() {
  window.addEventListener("error", (e) => {
    push({
      t: "err",
      kind: "error",
      msg: sanitizeText(e?.message, 300),
      src: sanitizeText(e?.filename, 160),
      line: e?.lineno ?? null,
      col: e?.colno ?? null,
      ts: Date.now(),
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = sanitizeText(e?.reason?.message || e?.reason || "", 300);
    push({ t: "err", kind: "unhandledrejection", msg, ts: Date.now() });
  });
}

/* ==================== FPS ==================== */
function startFpsMeter() {
  if (!cfg.fpsEnabled || !("requestAnimationFrame" in window)) return;
  let frames = 0,
    lastSec = now();
  function loop(ts) {
    frames++;
    if (ts - lastSec >= 1000) {
      const fps = clamp(Math.round((frames * 1000) / (ts - lastSec)), 0, 120);
      store.fps.current = fps;
      store.fps.min = Math.min(store.fps.min, fps);
      store.fps.max = Math.max(store.fps.max, fps);
      store.fps.sum += fps;
      store.fps.n += 1;
      frames = 0;
      lastSec = ts;
    }
    requestAnimationFrame(loop);
  }
  requestAnimationFrame(loop);
}

/* ==================== LONG TASKS ==================== */
function startLongTaskObserver() {
  if (!cfg.longTaskEnabled || !("PerformanceObserver" in window)) return;
  try {
    const po = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        const d = Math.round(e.duration);
        if (Number.isFinite(d)) {
          store.longTasks.push(clamp(d, 0, 2_147_483_647));
          if (store.longTasks.length > 120) store.longTasks.shift();
        }
      }
    });
    po.observe({ entryTypes: ["longtask"] });
  } catch {}
}

/* ==================== DOM (peso) ==================== */
function sampleDom() {
  if (!cfg.domSampleEnabled || !cfg.tableRootSelector) return;
  const el = document.querySelector(cfg.tableRootSelector);
  if (!el) return;
  const rows =
    el.querySelectorAll("tr").length ||
    el.querySelectorAll("[role='row']").length;
  const cells =
    el.querySelectorAll("td,th").length ||
    el.querySelectorAll("[role='cell']").length;
  store.dom.rows = clamp(rows, 0, 2_147_483_647);
  store.dom.cells = clamp(cells, 0, 2_147_483_647);
}

/* ==================== MEMÓRIA ==================== */
function sampleMemory() {
  try {
    if (!performance || !performance.memory) return null;
    const { usedJSHeapSize, totalJSHeapSize } = performance.memory;
    return {
      used_mb: clamp(
        Math.round(usedJSHeapSize / 1024 / 1024),
        0,
        2_147_483_647
      ),
      total_mb: clamp(
        Math.round(totalJSHeapSize / 1024 / 1024),
        0,
        2_147_483_647
      ),
    };
  } catch {
    return null;
  }
}

/* ==================== REDE ==================== */
function patchNetwork() {
  if (!cfg.networkEnabled || netPatched) return;
  const endpoint = resolveEndpoint();
  const telemetryUrl = joinUrl(cfg.apiBase, endpoint);
  const telePath = normalizePath(telemetryUrl);
  const shouldSkip = (url) =>
    normalizePath(typeof url === "string" ? url : url?.toString?.()) ===
    telePath;

  const origFetch = window.fetch;
  if (origFetch) {
    window.fetch = async function (url, opts = {}) {
      if (shouldSkip(url)) return origFetch(url, opts);
      const t0 = now();
      try {
        const res = await origFetch(url, opts);
        const t1 = now();
        const size = Number(res.headers?.get?.("content-length") || 0);
        const evt = {
          t: "net",
          method: (opts?.method || "GET").toUpperCase(),
          path: normalizePath(
            typeof url === "string" ? url : url?.toString?.()
          ),
          ms: clamp(Math.round(t1 - t0), 0, 2_147_483_647),
          status: res.status ?? 0,
          size: clamp(Number(size) || 0, 0, 2_147_483_647),
          ts: Date.now(),
        };
        store.apiCalls.push(evt);
        push(evt);
        return res;
      } catch (e) {
        const t1 = now();
        const evt = {
          t: "net",
          method: (opts?.method || "GET").toUpperCase(),
          path: normalizePath(
            typeof url === "string" ? url : url?.toString?.()
          ),
          ms: clamp(Math.round(t1 - t0), 0, 2_147_483_647),
          status: -1,
          size: 0,
          ts: Date.now(),
        };
        store.apiCalls.push(evt);
        push(evt);
        throw e;
      }
    };
  }

  if (window.XMLHttpRequest) {
    const XO = XMLHttpRequest.prototype.open;
    const XS = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__t = { method, url };
      return XO.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (body) {
      const url = this.__t?.url || "";
      if (!shouldSkip(url)) {
        const t0 = now();
        this.addEventListener("loadend", () => {
          const t1 = now();
          const size = Number(this.getResponseHeader?.("content-length") || 0);
          const evt = {
            t: "net",
            method: (this.__t?.method || "").toUpperCase(),
            path: normalizePath(url),
            ms: clamp(Math.round(t1 - t0), 0, 2_147_483_647),
            status: this.status ?? 0,
            size: clamp(Number(size) || 0, 0, 2_147_483_647),
            ts: Date.now(),
          };
          store.apiCalls.push(evt);
          push(evt);
        });
      }
      return XS.call(this, body);
    };
  }
  netPatched = true;
}

/* ==================== RENDER COUNTER + STORM ==================== */
function tickStorm() {
  const STORM_WIN_MS = 2000,
    STORM_THRESHOLD = 25;
  if (!store.stormWinStart) store.stormWinStart = now();
  const t = now();
  if (t - store.stormWinStart > STORM_WIN_MS) {
    if (store.stormCount > STORM_THRESHOLD)
      push({
        t: "hint",
        kind: "render-storm",
        count: store.stormCount,
        win_ms: STORM_WIN_MS,
        ts: Date.now(),
      });
    store.stormWinStart = t;
    store.stormCount = 0;
  }
}

export function useRenderCounter(name = "Leads.jsx") {
  const t0Ref = React.useRef(now());
  React.useEffect(() => {
    const took = Math.round(now() - t0Ref.current);
    store.renders += 1;
    store.renderMsTotal += took;
    store.renderMaxMs = Math.max(store.renderMaxMs, took);
    store.stormCount += 1;
    tickStorm();
    push({
      t: "render",
      name: sanitizeText(name, 64),
      ms: clamp(took, 0, 2_147_483_647),
      ts: Date.now(),
    });
  });
}

/* ==================== MARCADORES DE APP ==================== */
const __appMarks = new Map();

export function appMarkStart(label, meta = {}) {
  if (!enabled) return null;
  const id = `${sanitizeText(label, 64)}#${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  __appMarks.set(id, {
    t0: performance.now?.() ?? Date.now(),
    label: sanitizeText(label, 64),
    meta,
  });
  return id;
}
export function appMarkEnd(id) {
  if (!enabled || !id || !__appMarks.has(id)) return;
  const { t0, label, meta } = __appMarks.get(id);
  __appMarks.delete(id);
  const ms = Math.round((performance.now?.() ?? Date.now()) - t0);
  push({
    t: "app",
    label,
    ms: clamp(ms, 0, 2_147_483_647),
    meta,
    ts: Date.now(),
  });
}

/* ==================== BATCH & FLUSH ==================== */
function push(evt) {
  if (!enabled) return;
  try {
    const e = { ...evt };
    if (typeof e.msg === "string") e.msg = sanitizeText(e.msg, 300);
    if (typeof e.src === "string") e.src = sanitizeText(e.src, 160);
    if (typeof e.path === "string") e.path = sanitizeText(e.path, 256);
    if (typeof e.method === "string") e.method = sanitizeText(e.method, 16);
    if (Number.isFinite(e.ms)) e.ms = clamp(Math.trunc(e.ms), 0, 2_147_483_647);
    if (Number.isFinite(e.size))
      e.size = clamp(Math.trunc(e.size), 0, 2_147_483_647);
    if (Number.isFinite(e.status))
      e.status = clamp(Math.trunc(e.status), -1, 2_147_483_647);
    store.batch.push(e);
    if (store.batch.length > cfg.maxBatch * 4)
      store.batch.splice(0, store.batch.length - cfg.maxBatch * 2);
    store.dev.push(e);
    if (store.dev.length > 2000) store.dev.splice(0, store.dev.length - 2000);
  } catch {}
}
const byteLen = (obj) => {
  try {
    return new TextEncoder().encode(JSON.stringify(obj)).length;
  } catch {
    return 0;
  }
};

async function flush() {
  if (!enabled || store.hidden || !cfg.apiBase || store.batch.length === 0)
    return;

  sampleDom();
  const mem = sampleMemory();
  const avgFps = store.fps.n ? Math.round(store.fps.sum / store.fps.n) : null;

  const take = store.batch.splice(0, cfg.maxBatch);

  let payload = {
    empresa_id: cfg.empresaId ?? null,
    user_id: cfg.userId ?? null,
    page: cfg.page || "leads",
    client_version: cfg.clientVersion || "v0",
    sent_at: new Date().toISOString(),
    fps_min: store.fps.n ? store.fps.min : null,
    fps_avg: avgFps,
    longtasks: store.longTasks.slice(-20),
    dom: store.dom,
    memory: mem,
    renders: {
      count: store.renders,
      avg_ms: store.renders
        ? Math.round(store.renderMsTotal / store.renders)
        : 0,
      max_ms: store.renderMaxMs,
    },
    batch: take,
  };

  let bytes = byteLen(payload);
  if (bytes > cfg.maxPayloadBytes) {
    while (payload.batch.length > 5 && bytes > cfg.maxPayloadBytes) {
      payload.batch.pop();
      bytes = byteLen(payload);
    }
    if (bytes > cfg.maxPayloadBytes && payload.dom) {
      delete payload.dom;
      bytes = byteLen(payload);
    }
  }

  const url = joinUrl(cfg.apiBase, resolveEndpoint());

  try {
    const bodyStr = JSON.stringify(payload);
    const ok = navigator.sendBeacon?.(
      url,
      new Blob([bodyStr], { type: "application/json" })
    );
    if (ok) return;
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: bodyStr,
      credentials: "include",
      keepalive: true,
    });
  } catch {
    /* silencioso */
  }
}

function startFlusher() {
  setInterval(flush, cfg.flushIntervalMs);
  setTimeout(flush, 3000);
  window.addEventListener("beforeunload", () => {
    try {
      flush();
    } catch {}
  });
}

/* ==================== FLAG REMOTA (placeholder) ==================== */
async function fetchRemoteFlag() {
  return true;
}

/* ==================== DEV HELPERS (Console) ==================== */
function p95(arr) {
  if (!arr?.length) return null;
  const a = [...arr].sort((x, y) => x - y);
  return a[Math.floor(0.95 * (a.length - 1))];
}
function groupBy(arr, key) {
  const m = new Map();
  for (const it of arr || []) {
    const k = typeof key === "function" ? key(it) : it[key];
    if (k == null) continue;
    (m.get(k) || m.set(k, []).get(k)).push(it);
  }
  return m;
}

if (typeof window !== "undefined") {
  window.captaSummary = function captaSummary() {
    try {
      const dev = store.dev || [];

      const apps = dev.filter((e) => e?.t === "app" && Number.isFinite(e.ms));
      const byLabel = groupBy(apps, (e) => e.label);
      const appStats = {};
      for (const [label, items] of byLabel.entries()) {
        const ms = items.map((e) => e.ms);
        appStats[label] = {
          count: ms.length,
          p50_ms: p95(ms.slice(0, Math.ceil(ms.length * 0.5))),
          p95_ms: p95(ms),
          max_ms: Math.max(...ms),
        };
      }

      const nets = dev.filter((e) => e?.t === "net" && Number.isFinite(e.ms));
      const byPath = groupBy(nets, (e) => e.path);
      const netStats = {};
      for (const [path, items] of byPath.entries()) {
        const ms = items.map((e) => e.ms);
        netStats[path] = {
          count: ms.length,
          p95_ms: p95(ms),
          max_ms: Math.max(...ms),
          slow_count_800ms: items.filter((x) => x.ms >= 800).length,
        };
      }

      const renderInfo = {
        count: store.renders,
        avg_ms: store.renders
          ? Math.round(store.renderMsTotal / store.renders)
          : 0,
        max_ms: store.renderMaxMs,
      };

      return {
        client_version: cfg.clientVersion,
        app: appStats,
        net: netStats,
        render: renderInfo,
        dom_last_sample: store.dom,
        fps_avg: store.fps.n ? Math.round(store.fps.sum / store.fps.n) : null,
      };
    } catch (e) {
      console.error("captaSummary error:", e);
      return null;
    }
  };
}

/* ====== captaSummaryV2: semáforo + metas + top APIs ====== */
if (typeof window !== "undefined") {
  window.captaSummaryV2 = function captaSummaryV2(
    goals = {
      p95_render_ms: 120,
      p95_api_ms: 800,
      max_dom_cells: 5000,
      min_fps_avg: 50,
    }
  ) {
    try {
      const base = window.captaSummary?.() || {};
      const render = base.render || { count: 0, avg_ms: 0, max_ms: 0 };
      const net = base.net || {};
      const dom = base.dom_last_sample || { rows: 0, cells: 0 };
      const fps_avg = base.fps_avg ?? null;

      const dev = (window.__capta_dev = window.__capta_dev || store?.dev || []);
      const netArr = dev
        .filter((e) => e?.t === "net" && Number.isFinite(e.ms))
        .map((e) => e.ms);
      const appArr = dev
        .filter((e) => e?.t === "app" && Number.isFinite(e.ms))
        .map((e) => e.ms);
      const rendersArr = dev
        .filter((e) => e?.t === "render" && Number.isFinite(e.ms))
        .map((e) => e.ms);

      const calcP95 = (arr) =>
        arr?.length
          ? [...arr].sort((x, y) => x - y)[Math.floor(0.95 * (arr.length - 1))]
          : null;

      const p95_api = calcP95(netArr);
      const p95_render = calcP95(rendersArr) ?? (render.avg_ms || null);
      const p95_app = calcP95(appArr); // informativo

      function status3(v, ok, warn, invert = false) {
        if (v == null) return { level: "desconhecido", color: "gray" };
        if (!invert) {
          if (v <= ok) return { level: "OK", color: "green" };
          if (v <= warn) return { level: "Alerta", color: "yellow" };
          return { level: "Crítico", color: "red" };
        } else {
          if (v >= ok) return { level: "OK", color: "green" };
          if (v >= warn) return { level: "Alerta", color: "yellow" };
          return { level: "Crítico", color: "red" };
        }
      }

      const s_render = status3(
        p95_render ?? render.avg_ms,
        goals.p95_render_ms,
        goals.p95_render_ms * 2
      );
      const s_api = status3(p95_api, goals.p95_api_ms, goals.p95_api_ms * 1.5);
      const s_dom = status3(
        dom.cells,
        goals.max_dom_cells,
        goals.max_dom_cells * 1.5
      );
      const s_fps = status3(
        fps_avg,
        goals.min_fps_avg,
        Math.max(30, goals.min_fps_avg - 10),
        true
      );

      const longtasks = (store?.longTasks || []).slice(-50);
      const longMax = longtasks.length ? Math.max(...longtasks) : 0;
      const longCount200 = longtasks.filter((x) => x >= 200).length;

      const tips = [];
      if (s_render.level === "Crítico")
        tips.push(
          "Render alto: memoizar, reduzir re-renderes, dividir listas grandes."
        );
      if (s_api.level === "Crítico")
        tips.push("API lenta: indexar, paginar, reduzir payload.");
      if (s_dom.level === "Crítico")
        tips.push("DOM pesado: paginar, esconder colunas pouco usadas.");
      if (s_fps.level === "Crítico")
        tips.push(
          "FPS baixo: tirar trabalho do main thread; debounces em filtros."
        );
      if (longCount200 > 5)
        tips.push(
          "Muitas long tasks: loops, JSONs enormes, formatadores pesados."
        );

      // Top APIs
      const top = Object.entries(net)
        .map(([path, v]) => ({
          path,
          p95_ms: v?.p95_ms ?? null,
          count: v?.count ?? 0,
        }))
        .filter((x) => x.p95_ms != null)
        .sort((a, b) => (b.p95_ms || 0) - (a.p95_ms || 0))
        .slice(0, 3);

      // Modo DEMO: se nada coletado ainda, gera números amigáveis p/ visualizar UI
      const demo = localStorage.getItem(FLAG_DEMO) === "1";
      const demoPatch = (x, low, high) =>
        x ?? Math.round(low + Math.random() * (high - low));

      return demo
        ? {
            p95_render_ms: demoPatch(p95_render, 50, 300),
            p95_api_ms: demoPatch(p95_api, 200, 1500),
            dom_cells: demoPatch(dom.cells, 1200, 9000),
            fps_avg: demoPatch(fps_avg, 35, 60),
            longtasks_last50: demoPatch(longtasks.length, 0, 18),
            longtask_max_ms: demoPatch(longMax, 40, 900),
            status: { render: s_render, api: s_api, dom: s_dom, fps: s_fps },
            goals,
            top_api: top.length
              ? top
              : [
                  {
                    path: "/api/leads",
                    p95_ms: demoPatch(null, 450, 1200),
                    count: 120,
                  },
                  {
                    path: "/api/busca",
                    p95_ms: demoPatch(null, 300, 900),
                    count: 75,
                  },
                  {
                    path: "/api/whatsapp/badges",
                    p95_ms: demoPatch(null, 150, 600),
                    count: 40,
                  },
                ],
            tips: tips.length ? tips : ["Tudo dentro das metas no momento."],
          }
        : {
            p95_render_ms: p95_render,
            p95_api_ms: p95_api,
            dom_cells: dom.cells,
            fps_avg,
            longtasks_last50: longtasks.length,
            longtask_max_ms: longMax,
            status: { render: s_render, api: s_api, dom: s_dom, fps: s_fps },
            goals,
            top_api: top,
            tips,
          };
    } catch (e) {
      console.error("captaSummaryV2 error:", e);
      return null;
    }
  };
}

/* ==================== INIT ==================== */
export async function initLeadsTelemetry(userCfg = {}) {
  if (started) return;
  started = true;
  cfg = { ...defaultCfg, ...userCfg };
  if (
    !userCfg.clientVersion &&
    typeof window !== "undefined" &&
    window.CAPTA_MODE
  ) {
    cfg.clientVersion = window.CAPTA_MODE; // ex.: "turbo-perf-lite"
  }
  const local = localStorage.getItem(FLAG_LOCAL);
  enabled =
    local === "1" ? true : (await fetchRemoteFlag()) && rand() < cfg.sampleRate;
  if (!enabled) return;

  installVisibilityHandler();
  installErrorHandlers();
  patchNetwork();
  startFpsMeter();
  startLongTaskObserver();
  startFlusher();
}

/* ======================================================================= */
/* ========================== HUD VISUAL EMBUTIDO ========================= */
/* ======================================================================= */
/**
 * TelemetryHUD — painel “glass”, moderno e leigo-friendly:
 * - Semáforo geral (pior KPI)
 * - KPIs grandes (Render p95, API p95, DOM, FPS)
 * - Sparkline (linha com gradiente) de API p95
 * - Barra de progresso para DOM (capacidade)
 * - Top APIs + Dicas + botão "Copiar diagnóstico"
 * - Skeleton enquanto carrega
 * Ative DEMO: localStorage.CAPTA_TELEMETRY_DEMO = "1"
 */
export function TelemetryHUD({
  goals = {
    p95_render_ms: 120,
    p95_api_ms: 800,
    max_dom_cells: 5000,
    min_fps_avg: 50,
  },
  refreshMs = 3000,
  position = "bottom-right", // "bottom-left" | "top-right" | "top-left"
}) {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [histApi, setHistApi] = React.useState([]);

  React.useEffect(() => {
    let alive = true;
    function tick() {
      try {
        const res =
          (window.captaSummaryV2
            ? window.captaSummaryV2(goals)
            : window.captaSummary?.()) || null;
        if (!alive) return;
        setData(res);
        const v = res?.p95_api_ms;
        if (Number.isFinite(v)) setHistApi((p) => [...p, v].slice(-32));
      } catch {}
    }
    tick();
    const id = setInterval(tick, refreshMs);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [goals, refreshMs]);

  const posClass =
    {
      "bottom-right": "right-4 bottom-4",
      "bottom-left": "left-4 bottom-4",
      "top-right": "right-4 top-4",
      "top-left": "left-4 top-4",
    }[position] || "right-4 bottom-4";

  const colorMap = {
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    red: "bg-red-500",
    gray: "bg-gray-400",
  };
  const chipMap = {
    OK: "bg-emerald-100 text-emerald-700 border-emerald-200",
    Alerta: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Crítico: "bg-red-100 text-red-700 border-red-200",
    desconhecido: "bg-gray-100 text-gray-700 border-gray-200",
  };
  const colorOf = (s) => s?.color || "gray";
  const levelOf = (s) => s?.level || "desconhecido";
  const overallLevel = (d) => {
    const order = { OK: 0, Alerta: 1, Crítico: 2, desconhecido: 1 };
    const lvls = [
      d?.status?.render,
      d?.status?.api,
      d?.status?.dom,
      d?.status?.fps,
    ].map(levelOf);
    return lvls.reduce(
      (acc, cur) => (order[cur] > order[acc] ? cur : acc),
      "desconhecido"
    );
  };

  const Skeleton = () => (
    <div className="animate-pulse">
      <div className="h-6 w-40 bg-gray-200 rounded mb-3" />
      <div className="grid grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-3 rounded-2xl border bg-white">
            <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
            <div className="h-7 w-20 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="p-3 rounded-xl border bg-gray-50">
          <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
          <div className="h-10 w-full bg-gray-200 rounded" />
        </div>
        <div className="p-3 rounded-xl border bg-gray-50">
          <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-4 w-full bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const Badge = ({ color = "gray", label, value, suffix = "" }) => (
    <div className="flex flex-col p-3 rounded-2xl shadow bg-white border min-w-[150px]">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span
          className={`inline-block w-2.5 h-2.5 rounded-full ${
            colorMap[color] || colorMap.gray
          }`}
        />
        <span>{label}</span>
      </div>
      <div className="text-3xl font-semibold mt-1">
        {value == null ? "—" : value}
        {suffix}
      </div>
    </div>
  );

  const Sparkline = ({ values = [], height = 48 }) => {
    const h = Math.max(height, 10);
    const w = Math.max(values.length * 12, 144);
    if (!values.length)
      return (
        <div className="h-12 text-xs text-gray-400 flex items-center">
          sem dados
        </div>
      );
    const min = Math.min(...values),
      max = Math.max(...values),
      rng = Math.max(max - min, 1);
    const pts = values
      .map((v, i) => {
        const x = (i / (values.length - 1 || 1)) * (w - 8) + 4;
        const y = h - 6 - ((v - min) / rng) * (h - 16);
        return `${x},${y}`;
      })
      .join(" ");
    // área sob a linha
    const area = `4,${h - 6} ${pts} ${w - 4},${h - 6}`;
    return (
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12">
        <defs>
          <linearGradient id="captaLine" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <polygon
          points={area}
          fill="url(#captaLine)"
          className="text-blue-400"
        />
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          points={pts}
          className="text-blue-600"
        />
      </svg>
    );
  };

  const DomBar = ({ value = 0, max = 5000 }) => {
    const pct = Math.max(0, Math.min(100, Math.round((value / max) * 100)));
    return (
      <div>
        <div className="flex justify-between text-[11px] text-gray-500 mb-1">
          <span>{value?.toLocaleString?.() ?? "—"} células</span>
          <span>{pct}% da meta</span>
        </div>
        <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full ${
              pct < 80
                ? "bg-emerald-500"
                : pct < 120
                ? "bg-yellow-500"
                : "bg-red-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  function copyDiag() {
    try {
      const d = data || {};
      const lines = [
        "CAPTA • Diagnóstico",
        `Render p95: ${d.p95_render_ms ?? "—"} ms`,
        `API p95: ${d.p95_api_ms ?? "—"} ms`,
        `DOM: ${d.dom_cells ?? "—"}`,
        `FPS: ${d.fps_avg ?? "—"}`,
        `Top: ${
          (d.top_api || []).map((x) => `${x.p95_ms}ms ${x.path}`).join(" | ") ||
          "—"
        }`,
        `Dicas: ${(d.tips || []).join(" • ") || "—"}`,
      ];
      navigator.clipboard?.writeText(lines.join("\n"));
    } catch {}
  }

  const header = (
    <div className="rounded-3xl border shadow-2xl bg-white/90 backdrop-blur p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">Medidor Turbo — Leads</div>
          <span
            className={`px-2 py-1 text-xs rounded-full border ${
              chipMap[overallLevel(data)] || chipMap.desconhecido
            }`}
          >
            {overallLevel(data)}
          </span>
        </div>
        <div className="text-[11px] text-gray-500">
          Atualiza a cada {Math.round(refreshMs / 1000)}s
        </div>
      </div>

      {!data ? (
        <Skeleton />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Badge
              color={colorOf(data?.status?.render)}
              label="Render p95"
              value={data?.p95_render_ms?.toLocaleString?.() ?? null}
              suffix=" ms"
            />
            <Badge
              color={colorOf(data?.status?.api)}
              label="API p95"
              value={data?.p95_api_ms?.toLocaleString?.() ?? null}
              suffix=" ms"
            />
            <div className="p-3 rounded-2xl shadow bg-white border min-w-[150px]">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full ${
                    colorMap[colorOf(data?.status?.dom)] || colorMap.gray
                  }`}
                />
                <span>DOM (células)</span>
              </div>
              <div className="mt-2">
                <DomBar
                  value={data?.dom_cells || 0}
                  max={goals.max_dom_cells}
                />
              </div>
            </div>
            <Badge
              color={colorOf(data?.status?.fps)}
              label="FPS médio"
              value={data?.fps_avg ?? null}
            />
          </div>
        </>
      )}
    </div>
  );

  const analise = !data ? null : (
    <div className="mt-3 rounded-3xl border bg-white/90 backdrop-blur p-4 shadow-xl">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="p-3 rounded-xl bg-gray-50 border">
          <div className="text-xs text-gray-600 mb-1">Tendência — API p95</div>
          <Sparkline values={histApi} />
        </div>
        <div className="p-3 rounded-xl bg-gray-50 border">
          <div className="text-xs text-gray-600 mb-1">Top API por p95</div>
          <div className="text-xs">
            {(data.top_api || []).length ? (
              data.top_api.map((x) => (
                <div key={x.path} className="truncate">
                  <b>{x.p95_ms ?? "—"} ms</b> —{" "}
                  <span title={x.path}>{x.path}</span>
                </div>
              ))
            ) : (
              <div>—</div>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 rounded-xl bg-yellow-50 border border-yellow-200">
        <div className="text-xs font-semibold text-yellow-800 mb-1">
          Dicas rápidas
        </div>
        <ul className="list-disc ml-4 text-xs text-yellow-800 space-y-1">
          {(data.tips?.length
            ? data.tips
            : ["Tudo dentro das metas ou sem dados suficientes."]
          ).map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-gray-600">
        <div>
          Meta: render p95 &lt; {goals.p95_render_ms} ms • API p95 &lt;{" "}
          {goals.p95_api_ms} ms • DOM &lt;{" "}
          {Number(goals.max_dom_cells).toLocaleString()} • FPS ≥{" "}
          {goals.min_fps_avg}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 rounded-lg border bg-white hover:bg-gray-50"
            onClick={copyDiag}
          >
            Copiar diagnóstico
          </button>
          <button
            className="px-2 py-1 rounded-lg border bg-white hover:bg-gray-50"
            onClick={() => setHistApi([])}
          >
            Limpar tendência
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`fixed z-[9999] ${posClass} select-none`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 rounded-full shadow bg-blue-600 text-white text-sm hover:bg-blue-700"
        title="Abrir Telemetria"
      >
        {open ? "Fechar Telemetria" : "Telemetria"}
      </button>
      {open && (
        <div className="mt-2 w-[420px] max-w-[92vw]">
          {header}
          {analise}
        </div>
      )}
    </div>
  );
}
